import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  BaseHttpService,
  IBaseResponse,
  IListRequest,
  IListResponse,
  IPaginatedResponse,
} from 'O3_core';
import {
  ICloseTicketRequest,
  ICreateTicketRequest,
  IRejectTicketRequest,
  ITicket,
  ITicketConsoleDetail,
  ITicketDetail,
  ITriageTicketRequest,
} from '../models/ticket.interface';

/**
 * `list()` and `getById()` inherited from `BaseHttpService` hit the *console*
 * endpoints (`POST /filter`, `GET /:id`), which are scope-gated. The `my*`
 * methods are the reporter-facing pair and are reachable by any authenticated
 * user — keeping them as separate methods rather than a flag means a caller
 * cannot accidentally request the privileged variant.
 */
@Injectable({ providedIn: 'root' })
export class TicketService extends BaseHttpService<ITicket> {
  constructor() {
    super({ service: 'support', feature: 'ticket', version: 'v1' });
  }

  // ─── Reporter ────────────────────────────────────────────

  listMine(data: IListRequest): Observable<IPaginatedResponse<ITicket>> {
    return this.post<IListResponse<ITicket>>(
      `${this.apiUrl}/my/filter`,
      data,
    ).pipe(map((response) => response.data));
  }

  getMineById(id: string): Observable<ITicketDetail> {
    return this.get<IBaseResponse<ITicketDetail>>(
      `${this.apiUrl}/my/${id}`,
    ).pipe(map((response) => response.data));
  }

  /** Reporter signs off on the proposed solution. Terminal. */
  acceptSolution(id: string): Observable<ITicketDetail> {
    return this.patch<IBaseResponse<ITicketDetail>>(
      `${this.apiUrl}/my/${id}/accept`,
      {},
    ).pipe(map((response) => response.data));
  }

  /**
   * Reporter rejects the proposed solution. Server-side this also demotes the
   * solution comment back to ordinary discussion.
   */
  rejectSolution(
    id: string,
    data: IRejectTicketRequest,
  ): Observable<ITicketDetail> {
    return this.patch<IBaseResponse<ITicketDetail>>(
      `${this.apiUrl}/my/${id}/reject`,
      data,
    ).pipe(map((response) => response.data));
  }

  fileTicket(data: ICreateTicketRequest): Observable<ITicketDetail> {
    return this.post<IBaseResponse<ITicketDetail>>(this.apiUrl, data).pipe(
      map((response) => response.data),
    );
  }

  // ─── Console ─────────────────────────────────────────────

  getDetailById(id: string): Observable<ITicketConsoleDetail> {
    return this.get<IBaseResponse<ITicketConsoleDetail>>(
      `${this.apiUrl}/${id}`,
    ).pipe(map((response) => response.data));
  }

  triage(
    id: string,
    data: ITriageTicketRequest,
  ): Observable<ITicketConsoleDetail> {
    return this.patch<IBaseResponse<ITicketConsoleDetail>>(
      `${this.apiUrl}/${id}/triage`,
      data,
    ).pipe(map((response) => response.data));
  }

  escalate(id: string): Observable<ITicketConsoleDetail> {
    return this.transition(id, 'escalate');
  }

  resolve(id: string): Observable<ITicketConsoleDetail> {
    return this.transition(id, 'resolve');
  }

  /**
   * Support proposes a solution and hands the ticket to the reporter. The
   * solution text is mandatory — it becomes the comment the reporter signs
   * off on.
   */
  close(
    id: string,
    data: ICloseTicketRequest,
  ): Observable<ITicketConsoleDetail> {
    return this.patch<IBaseResponse<ITicketConsoleDetail>>(
      `${this.apiUrl}/${id}/close`,
      data,
    ).pipe(map((response) => response.data));
  }

  reopen(id: string): Observable<ITicketConsoleDetail> {
    return this.transition(id, 'reopen');
  }

  private transition(
    id: string,
    action: 'escalate' | 'resolve' | 'reopen',
  ): Observable<ITicketConsoleDetail> {
    return this.patch<IBaseResponse<ITicketConsoleDetail>>(
      `${this.apiUrl}/${id}/${action}`,
      {},
    ).pipe(map((response) => response.data));
  }
}
